"use client";

import React, { createContext, useContext, useState, useCallback, type ReactNode, type MutableRefObject } from "react";
import type { TradingChartRef } from "@/components/TradingChart/TradingChart";
import { apiClient } from "@/lib/api-client";

export type FastForwardState = 'idle' | 'loading' | 'streaming' | 'complete' | 'error';

export interface FastForwardProgress {
  state: FastForwardState;
  progress?: number;
  error?: string;
}

interface FastForwardContextType {
  // Current state
  state: FastForwardState;
  progress?: number;
  error?: string;
  isVisible: boolean;
  hasBeenTriggered: boolean;
  chartRef: MutableRefObject<TradingChartRef | null> | null;
  sessionId: string | null;
  toolCallId: string | null;
  executedToolCallIds: Set<string>;
  toolCallTimeArgs: Map<string, number>;
  toolCallActions: Map<string, string>;
  startFastForward: () => Promise<void>;
  setVisible: (visible: boolean) => void;
  setChartRef: (ref: MutableRefObject<TradingChartRef | null>) => void;
  setDemoActionData: (sessionId: string, toolCallId: string) => void;
  setToolCallTimeArg: (toolCallId: string, timeHours: number) => void;
  setToolCallAction: (toolCallId: string, action: string) => void;
  resetState: () => void;
  resetStateForNewTool: () => void;
  updateState: (progress: FastForwardProgress) => void;
  markToolCallExecuted: (toolCallId: string) => void;
  isToolCallExecuted: (toolCallId: string) => boolean;
}

const FastForwardContext = createContext<FastForwardContextType | null>(null);

export function FastForwardProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<FastForwardState>('idle');
  const [progress, setProgress] = useState<number | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenTriggered, setHasBeenTriggered] = useState(false);
  const [chartRef, setChartRef] = useState<MutableRefObject<TradingChartRef | null> | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [toolCallId, setToolCallId] = useState<string | null>(null);
  const [executedToolCallIds, setExecutedToolCallIds] = useState<Set<string>>(new Set());
  const [toolCallTimeArgs, setToolCallTimeArgs] = useState<Map<string, number>>(new Map());
  const [toolCallActions, setToolCallActions] = useState<Map<string, string>>(new Map());

  const updateState = useCallback((progressUpdate: FastForwardProgress) => {
    setState(progressUpdate.state);
    setProgress(progressUpdate.progress);
    setError(progressUpdate.error);
    
    // Mark the current active tool as executed and call backend API when complete
    if (progressUpdate.state === 'complete' && sessionId && toolCallId) {
      // Mark the active toolCallId as executed
      setExecutedToolCallIds(prev => new Set([...prev, toolCallId]));
      
      // Call backend API with the appropriate action
      const action = toolCallActions.get(toolCallId) || 'fast_forward';
      apiClient.sendDemoAction(sessionId, toolCallId, action)
        .then(() => {
        })
        .catch((err) => {
          // Don't fail the entire operation if backend call fails
        });
    }
  }, [sessionId, toolCallId, toolCallActions]);

  const startFastForward = useCallback(async () => {
    if (!chartRef?.current || state === 'loading' || state === 'streaming') {
      return;
    }

    try {
      // Get the time argument for the current tool call, default to 100 hours
      const timeHours = toolCallId ? toolCallTimeArgs.get(toolCallId) || 100 : 100;
      await chartRef.current.fastForward(timeHours, updateState);
    } catch (err) {
      updateState({
        state: 'error',
        error: err instanceof Error ? err.message : 'Fast forward failed'
      });
    }
  }, [chartRef, state, toolCallId, toolCallTimeArgs, updateState]);

  const resetState = useCallback(() => {
    setState('idle');
    setProgress(undefined);
    setError(undefined);
  }, []);

  const setVisible = useCallback((visible: boolean) => {
    setIsVisible(visible);
    if (visible) {
      setHasBeenTriggered(true);
    }
    if (!visible) {
      resetState();
    }
  }, [resetState]);

  const setChartRefCallback = useCallback((ref: MutableRefObject<TradingChartRef | null>) => {
    setChartRef(ref);
  }, []);

  const setDemoActionData = useCallback((sessionId: string, toolCallId: string) => {
    setSessionId(sessionId);
    setToolCallId(toolCallId);
  }, []);

  const resetStateForNewTool = useCallback(() => {
    setState('idle');
    setProgress(undefined);
    setError(undefined);
    // Keep hasBeenTriggered and isVisible for toolbar functionality
  }, []);

  const markToolCallExecuted = useCallback((toolCallId: string) => {
    setExecutedToolCallIds(prev => new Set([...prev, toolCallId]));
  }, []);

  const isToolCallExecuted = useCallback((toolCallId: string) => {
    return executedToolCallIds.has(toolCallId);
  }, [executedToolCallIds]);

  const setToolCallTimeArg = useCallback((toolCallId: string, timeHours: number) => {
    setToolCallTimeArgs(prev => new Map(prev.set(toolCallId, timeHours)));
  }, []);

  const setToolCallAction = useCallback((toolCallId: string, action: string) => {
    setToolCallActions(prev => new Map(prev.set(toolCallId, action)));
  }, []);

  const value: FastForwardContextType = {
    state,
    progress,
    error,
    isVisible,
    hasBeenTriggered,
    chartRef,
    sessionId,
    toolCallId,
    executedToolCallIds,
    toolCallTimeArgs,
    toolCallActions,
    startFastForward,
    resetState,
    resetStateForNewTool,
    setVisible,
    setChartRef: setChartRefCallback,
    setDemoActionData,
    setToolCallTimeArg,
    setToolCallAction,
    updateState,
    markToolCallExecuted,
    isToolCallExecuted,
  };

  return (
    <FastForwardContext.Provider value={value}>
      {children}
    </FastForwardContext.Provider>
  );
}

export function useFastForward() {
  const context = useContext(FastForwardContext);
  if (!context) {
    throw new Error('useFastForward must be used within a FastForwardProvider');
  }
  return context;
}
